import React, { useState } from "react";
import "./Sponsor.css";

const SPONSORSHIP_LEVELS = [
    {
        name: "Mission Supporter",
        amount: "$1,000+",
        description:
            "Support STEMX365's mission to make space, robotics, and STEM education more accessible.",
        features: [
            "STEMX365 sponsor recognition",
            "Recognition on our website",
            "Digital sponsor certificate",
            "Updates about STEMX365 programs",
        ],
    },
    {
        name: "Education Partner",
        amount: "$5,000+",
        description:
            "Help students gain hands-on experience with programming, robotics, simulation, and space technology.",
        features: [
            "Everything in Mission Supporter",
            "Featured sponsor recognition",
            "Recognition in selected STEMX365 materials",
            "Support for student access",
            "Program impact updates",
        ],
        popular: true,
    },
    {
        name: "Space Technology Partner",
        amount: "$10,000+",
        description:
            "Help expand access to advanced space robotics and simulation experiences for students.",
        features: [
            "Everything in Education Partner",
            "Premium sponsor recognition",
            "Featured partnership opportunity",
            "Support for space robotics laboratories",
            "Opportunities for employee engagement",
        ],
    },
    {
        name: "Mission Sponsor",
        amount: "$25,000+",
        description:
            "Become a major supporter of the next generation of space engineers, scientists, and innovators.",
        features: [
            "Everything in Space Technology Partner",
            "Premier sponsor recognition",
            "Major program sponsorship",
            "Customized partnership opportunities",
            "Direct impact reporting",
            "Strategic STEMX365 collaboration",
        ],
    },
];

const Sponsor = () => {
    const [formData, setFormData] = useState({
        organization: "",
        name: "",
        email: "",
        phone: "",
        sponsorshipLevel: "",
        message: "",
    });

    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (event) => {
        const { name, value } = event.target;

        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setError("");
        setSubmitted(false);

        if (!formData.organization.trim()) {
            setError("Please enter your organization name.");
            return;
        }

        if (!formData.name.trim()) {
            setError("Please enter your name.");
            return;
        }

        if (!formData.email.trim()) {
            setError("Please enter your email address.");
            return;
        }

        setLoading(true);

        try {
            /*
             * Change this endpoint if your Flask API uses
             * a different sponsorship/contact route.
             */
            const response = await fetch(
                "/api/sponsorships/inquiry",
                {
                    method: "POST",

                    headers: {
                        "Content-Type": "application/json",
                    },

                    credentials: "include",

                    body: JSON.stringify(formData),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.error ||
                        "Unable to submit your sponsorship inquiry."
                );
            }

            setSubmitted(true);

            setFormData({
                organization: "",
                name: "",
                email: "",
                phone: "",
                sponsorshipLevel: "",
                message: "",
            });
        } catch (err) {
            console.error("Sponsorship inquiry error:", err);

            setError(
                err.message ||
                    "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="sponsor-page">

            {/* =====================================================
                HERO
            ====================================================== */}

            <section className="sponsor-hero">

                <div className="sponsor-hero-overlay"></div>

                <div className="sponsor-hero-content">

                    <div className="sponsor-eyebrow">
                        PARTNER WITH STEMX365
                    </div>

                    <h1>
                        Help Build the
                        <span>
                            Next Generation of Space Innovators
                        </span>
                    </h1>

                    <p>
                        Become a STEMX365 sponsor and help students
                        gain access to space robotics, simulation,
                        programming, and real-world engineering
                        experiences.
                    </p>

                    <div className="hero-actions">

                        <a
                            href="#sponsorship-levels"
                            className="hero-primary-button"
                        >
                            Explore Sponsorship
                        </a>

                        <a
                            href="#sponsor-form"
                            className="hero-secondary-button"
                        >
                            Become a Partner
                        </a>

                    </div>

                </div>
            </section>


            {/* =====================================================
                INTRODUCTION
            ====================================================== */}

            <section className="sponsor-introduction">

                <div className="sponsor-container">

                    <div className="sponsor-intro-content">

                        <div className="sponsor-eyebrow">
                            WHY SPONSOR STEMX365?
                        </div>

                        <h2>
                            Invest in the Future of Space
                            Exploration
                        </h2>

                        <p>
                            The future of space exploration depends
                            on the engineers, scientists, programmers,
                            and innovators we inspire today.
                        </p>

                        <p>
                            STEMX365 creates opportunities for students
                            to learn through hands-on programming,
                            robotics, simulation, and space technology.
                            Sponsors help us expand these opportunities
                            and reach more students.
                        </p>

                        <p>
                            Your organization can become part of that
                            journey.
                        </p>

                    </div>

                    <div className="sponsor-impact-grid">

                        <div className="sponsor-impact-card">
                            <div className="sponsor-impact-icon">
                                🚀
                            </div>

                            <h3>
                                Space Education
                            </h3>

                            <p>
                                Help students experience technologies
                                inspired by real space missions.
                            </p>
                        </div>

                        <div className="sponsor-impact-card">
                            <div className="sponsor-impact-icon">
                                🤖
                            </div>

                            <h3>
                                Space Robotics
                            </h3>

                            <p>
                                Support hands-on programming and
                                robotics experiences.
                            </p>
                        </div>

                        <div className="sponsor-impact-card">
                            <div className="sponsor-impact-icon">
                                💻
                            </div>

                            <h3>
                                Digital Access
                            </h3>

                            <p>
                                Help make advanced STEM learning
                                accessible through the cloud.
                            </p>
                        </div>

                        <div className="sponsor-impact-card">
                            <div className="sponsor-impact-icon">
                                👩‍🚀
                            </div>

                            <h3>
                                Future Workforce
                            </h3>

                            <p>
                                Help prepare students for the growing
                                global space economy.
                            </p>
                        </div>

                    </div>

                </div>
            </section>


            {/* =====================================================
                WHAT SPONSORS SUPPORT
            ====================================================== */}

            <section className="sponsor-support">

                <div className="sponsor-container">

                    <div className="sponsor-section-heading">

                        <div className="sponsor-eyebrow">
                            YOUR SUPPORT
                        </div>

                        <h2>
                            What Your Sponsorship Can Help Build
                        </h2>

                        <p>
                            Sponsorship funding can help STEMX365
                            expand educational programs, technology,
                            and access for students.
                        </p>

                    </div>

                    <div className="support-grid">

                        <div className="support-item">
                            <span>01</span>
                            <h3>
                                Space Robotics Laboratories
                            </h3>
                            <p>
                                Expand virtual laboratories where
                                students can program and experiment
                                with space robots.
                            </p>
                        </div>

                        <div className="support-item">
                            <span>02</span>
                            <h3>
                                Student Access
                            </h3>
                            <p>
                                Help students who may not otherwise
                                have access to advanced STEM
                                opportunities.
                            </p>
                        </div>

                        <div className="support-item">
                            <span>03</span>
                            <h3>
                                New Educational Programs
                            </h3>
                            <p>
                                Support the development of new
                                space science, robotics, and
                                engineering experiences.
                            </p>
                        </div>

                        <div className="support-item">
                            <span>04</span>
                            <h3>
                                Technology Development
                            </h3>
                            <p>
                                Help us continue developing the
                                cloud infrastructure and simulation
                                technologies behind STEMX365.
                            </p>
                        </div>

                    </div>

                </div>
            </section>


            {/* =====================================================
                SPONSORSHIP LEVELS
            ====================================================== */}

            <section
                className="sponsorship-levels"
                id="sponsorship-levels"
            >

                <div className="sponsor-container">

                    <div className="sponsor-section-heading">

                        <div className="sponsor-eyebrow">
                            SPONSORSHIP OPPORTUNITIES
                        </div>

                        <h2>
                            Choose Your Level of Impact
                        </h2>

                        <p>
                            We offer several sponsorship levels,
                            with opportunities to create a
                            customized partnership around your
                            organization's goals.
                        </p>

                    </div>

                    <div className="sponsorship-grid">

                        {SPONSORSHIP_LEVELS.map((level) => (

                            <div
                                key={level.name}
                                className={
                                    level.popular
                                        ? "sponsorship-card popular"
                                        : "sponsorship-card"
                                }
                            >

                                {level.popular && (
                                    <div className="popular-badge">
                                        FEATURED
                                    </div>
                                )}

                                <div className="sponsorship-card-header">

                                    <h3>
                                        {level.name}
                                    </h3>

                                    <div className="sponsorship-amount">
                                        {level.amount}
                                    </div>

                                    <p>
                                        {level.description}
                                    </p>

                                </div>

                                <div className="sponsorship-divider"></div>

                                <ul>
                                    {level.features.map(
                                        (feature) => (
                                            <li key={feature}>
                                                <span>✓</span>
                                                {feature}
                                            </li>
                                        )
                                    )}
                                </ul>

                                <a
                                    href="#sponsor-form"
                                    className="sponsorship-button"
                                    onClick={() =>
                                        setFormData((previous) => ({
                                            ...previous,
                                            sponsorshipLevel:
                                                level.name,
                                        }))
                                    }
                                >
                                    Choose This Level
                                </a>

                            </div>

                        ))}

                    </div>

                </div>
            </section>


            {/* =====================================================
                CUSTOM PARTNERSHIP
            ====================================================== */}

            <section className="custom-partnership">

                <div className="custom-partnership-container">

                    <div>

                        <div className="sponsor-eyebrow">
                            CUSTOM PARTNERSHIPS
                        </div>

                        <h2>
                            Looking for a Different
                            Partnership?
                        </h2>

                        <p>
                            We welcome organizations interested in
                            creating customized sponsorships,
                            educational partnerships, technology
                            collaborations, equipment support, or
                            major gifts.
                        </p>

                    </div>

                    <a
                        href="#sponsor-form"
                        className="custom-partnership-button"
                    >
                        Let's Build a Partnership
                    </a>

                </div>

            </section>


            {/* =====================================================
                SPONSOR FORM
            ====================================================== */}

            <section
                className="sponsor-form-section"
                id="sponsor-form"
            >

                <div className="sponsor-container">

                    <div className="sponsor-form-layout">

                        <div className="sponsor-form-information">

                            <div className="sponsor-eyebrow">
                                BECOME A SPONSOR
                            </div>

                            <h2>
                                Let's Start a Conversation
                            </h2>

                            <p>
                                Tell us a little about your
                                organization and how you would
                                like to support STEMX365.
                            </p>

                            <p>
                                A member of our team will follow
                                up with you to discuss sponsorship
                                opportunities and partnership
                                options.
                            </p>

                            <div className="form-note">

                                <strong>
                                    Corporate & Organizational
                                    Partnerships
                                </strong>

                                <span>
                                    Customized sponsorship
                                    arrangements are available.
                                </span>

                            </div>

                        </div>


                        <div className="sponsor-form-card">

                            {submitted && (

                                <div className="sponsor-success">
                                    <div className="success-icon">
                                        ✓
                                    </div>

                                    <h3>
                                        Thank You!
                                    </h3>

                                    <p>
                                        Your sponsorship inquiry
                                        has been received. We will
                                        be in touch with you soon.
                                    </p>
                                </div>

                            )}

                            {!submitted && (

                                <form onSubmit={handleSubmit}>

                                    <div className="form-row">

                                        <div className="form-section">

                                            <label htmlFor="organization">
                                                Organization
                                                <span className="required">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                id="organization"
                                                name="organization"
                                                type="text"
                                                value={
                                                    formData.organization
                                                }
                                                onChange={handleChange}
                                                placeholder="Your organization"
                                                required
                                            />

                                        </div>

                                        <div className="form-section">

                                            <label htmlFor="name">
                                                Your Name
                                                <span className="required">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                id="name"
                                                name="name"
                                                type="text"
                                                value={
                                                    formData.name
                                                }
                                                onChange={handleChange}
                                                placeholder="Your name"
                                                required
                                            />

                                        </div>

                                    </div>


                                    <div className="form-row">

                                        <div className="form-section">

                                            <label htmlFor="email">
                                                Email
                                                <span className="required">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={
                                                    formData.email
                                                }
                                                onChange={handleChange}
                                                placeholder="you@example.com"
                                                required
                                            />

                                        </div>

                                        <div className="form-section">

                                            <label htmlFor="phone">
                                                Phone
                                                <span className="optional">
                                                    (optional)
                                                </span>
                                            </label>

                                            <input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                value={
                                                    formData.phone
                                                }
                                                onChange={handleChange}
                                                placeholder="Phone number"
                                            />

                                        </div>

                                    </div>


                                    <div className="form-section">

                                        <label htmlFor="sponsorshipLevel">
                                            Sponsorship Level
                                        </label>

                                        <select
                                            id="sponsorshipLevel"
                                            name="sponsorshipLevel"
                                            value={
                                                formData.sponsorshipLevel
                                            }
                                            onChange={handleChange}
                                        >
                                            <option value="">
                                                Select an option
                                            </option>

                                            {SPONSORSHIP_LEVELS.map(
                                                (level) => (
                                                    <option
                                                        key={
                                                            level.name
                                                        }
                                                        value={
                                                            level.name
                                                        }
                                                    >
                                                        {level.name} —{" "}
                                                        {level.amount}
                                                    </option>
                                                )
                                            )}

                                            <option value="Custom Partnership">
                                                Custom Partnership
                                            </option>
                                        </select>

                                    </div>


                                    <div className="form-section">

                                        <label htmlFor="message">
                                            Tell Us About Your
                                            Organization
                                            <span className="optional">
                                                (optional)
                                            </span>
                                        </label>

                                        <textarea
                                            id="message"
                                            name="message"
                                            value={
                                                formData.message
                                            }
                                            onChange={handleChange}
                                            placeholder="Tell us about your organization and how you would like to support STEMX365..."
                                            rows="6"
                                        />

                                    </div>


                                    {error && (
                                        <div className="sponsor-error">
                                            {error}
                                        </div>
                                    )}


                                    <button
                                        type="submit"
                                        className="sponsor-submit-button"
                                        disabled={loading}
                                    >
                                        {loading
                                            ? "Sending..."
                                            : "Start a Sponsorship Conversation"}
                                    </button>

                                    <div className="form-security">
                                        🔒 Your information will be
                                        used only to respond to your
                                        sponsorship inquiry.
                                    </div>

                                </form>

                            )}

                        </div>

                    </div>

                </div>
            </section>


            {/* =====================================================
                FINAL CTA
            ====================================================== */}

            <section className="sponsor-final-cta">

                <div>

                    <div className="sponsor-eyebrow">
                        BUILD THE FUTURE
                    </div>

                    <h2>
                        The Next Generation Is Ready
                    </h2>

                    <p>
                        Help give them the opportunity to reach
                        for the stars.
                    </p>

                    <a
                        href="#sponsor-form"
                        className="final-cta-button"
                    >
                        Become a STEMX365 Sponsor
                    </a>

                </div>

            </section>

        </main>
    );
};

export default Sponsor;